import { Box, Divider, Typography } from '@mui/material';
import { ScrollableView } from '../../../../Layout';
import { SectionContainer } from './section-container.component';
import { LabelColonValue } from './label-colon-value.component';
import { useL10n } from '@uplift/hooks';
import { useMemo } from 'react';
import { CalendarDateService } from '@uplift/services';
import { TimeFormats } from '@uplift/constants';
import { useTimezone } from '@uplift/contexts';
import { useSessionCptCodesQuery, useSessionDiagnosisQuery, useUserWithProfileQuery } from '@uplift/queries';
import { Session, sessionParticipantOptionsDict, sessionServiceOptionsDict } from '@uplift/api';
import { TimeRange } from '@uplift/types';

export type SessionNoteDetailSidebarProps = {
  session: Session;
  noteTime: TimeRange;
  patientId: string;
  mentalStatusExamNote?: string;
};

export const SessionNoteDetailSidebar: React.VFC<SessionNoteDetailSidebarProps> = ({ session, patientId, noteTime, mentalStatusExamNote }) => {
  const timezone = useTimezone();
  const l10n = useL10n();
  const { data: diagnosis } = useSessionDiagnosisQuery(session, { suspense: true });
  const { data: cptCodes } = useSessionCptCodesQuery(session, { suspense: true });
  const { data: patient } = useUserWithProfileQuery(patientId, { suspense: true });

  const patientName = useMemo(() => {
    const preferredName = patient?.profile?.preferredName ? `(${patient.profile?.preferredName})` : '';

    return `${patient?.user?.firstName} ${patient?.user?.lastName} ${preferredName}`;
  }, [ patient ]);

  const startDate = CalendarDateService.date(noteTime.start).tz(timezone);
  const endDate = CalendarDateService.date(noteTime.end).tz(timezone);
  const formattedStartTime = startDate.format(TimeFormats['h:mma']);
  const formattedEndTime = endDate.format(TimeFormats['h:mma']);

  if (!session) return null;

  const participant = session.participation ? sessionParticipantOptionsDict[session.participation] : null;
  const serviceType = session.serviceType ? sessionServiceOptionsDict[session.serviceType] : null;

  return (
    <>
      <Divider orientation="vertical"/>
      <ScrollableView style={{ flex: 'unset', width: '400px' }}>
        <Box px={3} pt={4} pb={1}>
          <Typography variant="h5" style={{ fontWeight: 450 }}>{patientName}</Typography>
        </Box>
        <Box >
          <SectionContainer title="Details">
            {participant && <LabelColonValue label={l10n.clinicalNotes.participants} value={participant} />}
            {serviceType && <LabelColonValue label={l10n.clinicalNotes.location} value={serviceType} />}
            <LabelColonValue label={l10n.clinicalNotes.addenda.startAndEndTime} value={`${formattedStartTime}–${formattedEndTime}`} />
          </SectionContainer>
          <SectionContainer title={l10n.clinicalNotes.diagnosis}>
            <Box display="flex" gap="1rem" flexDirection="column">
              {diagnosis?.map(({ name, id, code }) => {
                return <Typography key={id}>{code}&nbsp;{name}</Typography>;
              })}
              {!diagnosis?.length && <Typography>{l10n.clinicalNotes.none}</Typography>}
            </Box>
          </SectionContainer>
          <SectionContainer title="Mental Status Exam">
            <Typography>{mentalStatusExamNote}</Typography>
          </SectionContainer>
          <SectionContainer title="CPT Code" hideDivider>
            <Box display="flex" gap="1rem" flexDirection="column">
              {cptCodes?.map(({ name, id, code }) => {
                return <Typography key={id}>{`${code}:`}&nbsp;{name}</Typography>;
              })}
              {!cptCodes?.length && <Typography>{l10n.clinicalNotes.none}</Typography>}
            </Box>

          </SectionContainer>
        </Box>
      </ScrollableView>
    </>
  );
};
